import {
  ConversationMessage,
  getWorkspaceId,
  getChannel,
  updateChannel,
  useAppSelector,
  useLazyGetConversationMessageQuery,
  useLazyGetChannelDetailsQuery,
  dispatch,
} from '@zonofi/zono-money-store';
import { useState, useEffect } from 'react';

export const useGetConversationMessageList = ({
  conversationId,
  customerId,
  channelId,
}: {
  conversationId: string;
  customerId?: string;
  channelId?: string;
}) => {
  const workspaceId = useAppSelector(getWorkspaceId);
  const channel = useAppSelector(getChannel);

  const [fetchMessages, messagesResponse] =
    useLazyGetConversationMessageQuery();

  const [fetchChannelDetails] = useLazyGetChannelDetailsQuery();

  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [oldestFirst, setOldestFirst] = useState<boolean>(false);

  /* ---------------- CHANNEL LOGIC ---------------- */

  useEffect(() => {
    if (!workspaceId) return;

    // Priority: explicit customerId
    const effectiveCustomerId = customerId;

    if (!effectiveCustomerId) return;

    fetchChannelDetails({
      workspaceId,
      sellerWorkspaceId: workspaceId,
      ...(channelId
        ? { channelId }
        : { customerId: effectiveCustomerId, channelType: 'C' }),
    })
      .unwrap()
      .then((res) => {
        dispatch(updateChannel(res));
      })
      .catch((e) => console.error(e));
  }, [customerId, workspaceId]);

  /* ---------------- MESSAGES LOGIC ---------------- */

  const getMessages = () => {
    if (!conversationId || !workspaceId) return;

    fetchMessages({
      conversationId,
      sellerWorkspaceId: workspaceId,
    });
  };

  const handleSendMsg = (attempt = 0) => {
    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 700;

    fetchMessages({
      conversationId,
      sellerWorkspaceId: workspaceId,
    })
      .unwrap()
      .then((res) => {
        const latestCount = res?.data?.length ?? 0;
        const currentCount = messages?.length ?? 0;

        if (latestCount <= currentCount && attempt < MAX_RETRIES) {
          setTimeout(() => {
            handleSendMsg(attempt + 1);
          }, RETRY_DELAY_MS);
        }
      })
      .catch((e) => console.error(e));
  };

  const handleSortMessages = (oldestFirst: boolean) => {
    setMessages((prev) => {
      const sorted = [...prev].sort((a, b) =>
        oldestFirst
          ? Number(a.timetoken) - Number(b.timetoken)
          : Number(b.timetoken) - Number(a.timetoken)
      );
      return sorted;
    });
  };

  useEffect(() => {
    getMessages();
  }, [conversationId, refreshNonce]);

  useEffect(() => {
    if (messagesResponse.isSuccess && messagesResponse.data) {
      setMessages(messagesResponse.data.data ?? []);
    }
  }, [messagesResponse]);

  useEffect(() => {
    handleSortMessages(oldestFirst);
  }, [oldestFirst]);

  const refresh = () => {
    setRefreshNonce((prev) => prev + 1);
  };

  return {
    messages,
    channel,
    isLoading:
      messagesResponse.isUninitialized ||
      messagesResponse.isFetching ||
      messagesResponse.isLoading,
    handleSendMsg,
    setOldestFirst,
    oldestFirst,
    refetchMessages: refresh,
  };
};
